import React, { createContext, useContext, useState } from 'react';
import { UserSelection } from '../types';

interface SelectionContextType {
  selection: UserSelection;
  setCategory: (categoryId: string | null) => void;
  setSubcategory: (subcategoryId: string | null) => void;
  toggleUseCase: (useCaseId: string) => void;
  setExperienceLevel: (levelId: string | null) => void;
  resetSelection: () => void;
}

const initialSelection: UserSelection = {
  category: null,
  subcategory: null,
  useCases: [],
  experienceLevel: null
};

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

export const SelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selection, setSelection] = useState<UserSelection>(initialSelection);

  const setCategory = (categoryId: string | null) => {
    try {
      setSelection(prev => ({
        ...prev,
        category: categoryId,
        subcategory: null // Reset subcategory when category changes
      }));
    } catch (error) {
      console.error('Error setting category:', error);
      // Fallback to a safe state
      setSelection(initialSelection);
    }
  };

  const setSubcategory = (subcategoryId: string | null) => {
    try {
      setSelection(prev => ({
        ...prev,
        subcategory: subcategoryId
      }));
    } catch (error) {
      console.error('Error setting subcategory:', error);
    }
  };

  const toggleUseCase = (useCaseId: string) => {
    try {
      setSelection(prev => {
        const useCases = prev.useCases.includes(useCaseId)
          ? prev.useCases.filter(id => id !== useCaseId)
          : [...prev.useCases, useCaseId];
        return { ...prev, useCases };
      });
    } catch (error) {
      console.error('Error toggling use case:', error);
    }
  };

  const setExperienceLevel = (levelId: string | null) => {
    try {
      setSelection(prev => ({
        ...prev,
        experienceLevel: levelId
      }));
    } catch (error) {
      console.error('Error setting experience level:', error);
    }
  };

  const resetSelection = () => {
    try {
      setSelection(initialSelection);
    } catch (error) {
      console.error('Error resetting selection:', error);
    }
  };

  return (
    <SelectionContext.Provider value={{
      selection,
      setCategory,
      setSubcategory,
      toggleUseCase,
      setExperienceLevel,
      resetSelection
    }}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = (): SelectionContextType => {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};